<script setup>
import { useI18n } from 'vue-i18n'

import ResponsiveImage from '@/components/general/BaseImage.vue'
import { useModalStore } from '@/stores/ModalStore'
import { useMailSuffix } from "@/mixins/useMailSuffix";

const { t, locale } = useI18n()
const { openModal } = useModalStore()
</script>

<template>
  <div class="row">
    <div class="col-12">
      <h3
        class="mb-0 text-light text-left"
        role="heading"
        aria-level="3"
      >
        {{ t('customer_centre') }}
      </h3>
    </div>
    <div class="col-auto lg:col-6 pt-3">
      <div class="row">
        <div class="col-auto">
          <ResponsiveImage
            src="default-review-image.png"
            alt="customer centre"
            img-class="footer__person image-cover rounded-circle"
            sizes="10vw"
          />
        </div>
        <div class="col md:col-6">
          <p class="text-base text-warning">
            <!-- TODO: Add messenger link -->
            <a
              v-if="false"
              :href="`mailto:${t('support_email')}`"
              class="flex items-center mb-1 no-decoration"
            >
              <i class="mi-messenger icon-20 mr-3" />
              <span class="text-light">{{ t('messenger') }}</span>
            </a>
            <a
              :href="`mailto:${t('support_email') + useMailSuffix()}`"
              class="flex items-center mb-1 no-decoration"
            >
              <i class="mi-envelope icon-20 mr-3" />
              <span class="text-light">{{ t('support_email') + useMailSuffix() }}</span>
            </a>
            <a
              :href="`tel:${t('support_phone_link')}`"
              class="flex items-center no-decoration"
            >
              <i class="mi-phone icon-20 mr-3" />
              <span class="text-light">{{ t('support_phone') }}</span>
            </a>
            <a
              v-if="locale === 'cs' || locale === 'sk'"
              :href="t('whatsapp_link')"
              target="_blank"
              class="flex items-center mb-1 mt-1 no-decoration"
            >
              <i class="mi-whatsapp icon-20 mr-3" />
              <span class="text-light">{{ t('whatsapp') }}</span>
            </a>
          </p>
          <p class="text-xs">
            {{ t('working_hours_mo_th') }}<br>
            <template v-if="locale !== 'pl'">
              {{ t('working_hours_fr') }}
            </template>
          </p>
        </div>
        <div class="sm:col-3 xl:col-4">
          <p class="leading-lg text-xs">
            {{ t('producer') }}<br>
            {{ t('ic') }}<br>
            {{ t('dic') }}<br>
            {{ t('workshop') }}<br>
            {{ t('street') }}<br>
            {{ t('city') }}<br>
            {{ t('country') }}
          </p>
        </div>
      </div>
    </div>
    <div class="lg:col-6 pt-3 leading-lg">
      <p class="text-xs">
        <template v-if="locale !== 'cs' && locale !== 'de'">
          {{ t('according_to_act_on_registration_of_sales') }}
          <br>
          <br>
        </template>
        {{ t('this_site_is_used_to_provide_services') }}
        <i class="mi-cookies text-warning" />
        <span
          class="underline font-bold"
          @click="openModal({ id: 'modal-cookies' })"
        >
          {{ t('here') }}
        </span>
        <br>
        <br>
        <router-link :to="{ name: 'cookiesPath' }">
          {{ t('more_information') }}
        </router-link>
        <br>
        <br>
        {{ t('bug_noticed') }} ⚠️
        <router-link
          :to="{ name: 'bugReportPath' }"
          class="font-bold underline"
        >
          {{ t('bug_report_cta') }}
        </router-link>
      </p>
    </div>
  </div>
</template>

<i18n>
  {
    "cs": {
    "according_to_act_on_registration_of_sales": "Podle zákona o evidenci tržeb je prodávající povinen vystavit kupujícímu účtenku. Zároveň je povinen zaevidovat přijatou tržbu u správce daně online; v případě technického výpadku pak nejpozději do 48 hodin.",
    "bug_noticed": "Všimli jste si chyby na našem e-shopu? Dejte nám, prosím, vědět zde:",
    "bug_report_cta": "Nahlásit chybu",
    "city": "108 00, Praha 10",
    "country": "Česká republika",
    "customer_centre": "Zákaznické centrum",
    "dic": "DIČ: CZ24836451",
    "here": "zde.",
    "ic": "IČO: 24836451",
    "messenger": "Messenger",
    "more_information": "Další informace",
    "producer": "Mixit s.r.o.",
    "street": "Černokostelecká 604/116",
    "support_email": "info",
    "support_phone": "+420 210 012 140",
    "support_phone_link": "+420210012140",
    "this_site_is_used_to_provide_services": "Abychom vám mohli namixovat dokonalý zážitek při procházení našeho webu, používáme k personalizaci reklam a analýze návštěvnosti soubory cookies (ty čokoládové). Dříve udělený souhlas s jejich využíváním můžete odmítnout",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "V pátek 8.00–16.00.",
    "working_hours_mo_th": "Od pondělí do čtvrtka 8.00–16.30.",
    "workshop": "Provozovna:"
},
    "sk": {
    "according_to_act_on_registration_of_sales": "Podľa zákona o evidencii tržieb je predávajúci povinný vydať kupujúcemu doklad o kúpe. Zároveň je povinný zaevidovať prijatú tržbu u správcu dane online; v prípade technickej poruchy najneskôr do 48 hodín.",
    "bug_noticed": "Všimli ste si chybu na našom e-shope? Dajte nám, prosím, vedieť tu:",
    "bug_report_cta": "Nahlásiť chybu",
    "city": "108 00, Praha 10",
    "country": "Česká republika",
    "customer_centre": "Zákaznícke centrum",
    "dic": "DIČ: CZ24836451",
    "here": "Tu.",
    "ic": "IČO: 24836451",
    "messenger": "Messenger",
    "more_information": "Ďalšie informácie",
    "producer": "Mixit s.r.o.",
    "street": "Černokostelecká 604/116",
    "support_email": "info",
    "support_phone": "+421 221 025 208",
    "support_phone_link": "+420221025208",
    "this_site_is_used_to_provide_services": "Aby sme Vám mohli namixovať dokonalý zážitok pri prezeraní nášho webu, používame na personalizáciu reklám a analýzu návštevnosti súbory cookies (tie čokoládové). Predtým udelený súhlas s ich využívaním môžete odmietnuť",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "Piatok 8.00-16.00.",
    "working_hours_mo_th": "Od pondelka do štvrtka 8.00-16.30.",
    "workshop": "Prevádzka:"
},
    "pl": {
    "according_to_act_on_registration_of_sales": "Według prawa podatkowego, sprzedający jest zobowiązany do wystawienia rachunku kupującemu. Jednocześnie ma obowiązek zaewidować sprzedaż w systemie administracyjnym online; w przypadku problemów technicznych najpóźniej do 48 godzin.\n\n",
    "bug_noticed": "Zauważyłeś błąd w naszym sklepie internetowym? Daj nam znać tutaj:",
    "bug_report_cta": "Zgłoś błąd",
    "city": "108 00, Praga 10",
    "country": "Republika Czeska",
    "customer_centre": "Biuro obsługi klienta",
    "dic": "NIP: CZ24836451",
    "here": "tutaj.",
    "ic": "REGON: 24836451",
    "messenger": "Komunikator",
    "more_information": "Więcej informacji",
    "producer": "Mixit s.r.o.",
    "street": "Černokostelecká 604/116\n",
    "support_email": "biuro",
    "support_phone": "(+48) 22 300 17 88",
    "support_phone_link": "(+48) 22 300 17 88",
    "this_site_is_used_to_provide_services": "Aby zapewnić Ci doskonałe wrażenia z przeglądania, używamy plików cookie (tych czekoladowych) do personalizowania reklam i analizowania ruchu. Użytkownik może nie wyrazić zgody na ich użycie",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "",
    "working_hours_mo_th": "Od poniedziałku do piątku w godzinach 8:00-16:00.",
    "workshop": "Zakład produkcyjny:\n"
},
    "hu": {
    "according_to_act_on_registration_of_sales": "Az adásvételi nyilvántartásról szóló törvény szerint az eladó köteles nyugtát kiállítani a vevőnek. Ezzel egyidejűleg köteles az átvett értékesítést online regisztrálni az adóhatóságnál; technikai hiba esetén legkésőbb 48 órán belül.",
    "bug_noticed": "Észrevettél hibát a webshopban? Kérjük, jelezd nekünk itt:",
    "bug_report_cta": "Hiba bejelentése",
    "city": "108 00, Prága 10",
    "country": "Cseh Köztársaság",
    "customer_centre": "Ügyfélközpont",
    "dic": "ADÓSZÁM: CZ24836451",
    "here": "itt.",
    "ic": "ID: 24836451",
    "messenger": "Messenger",
    "more_information": "További információk",
    "producer": "Mixit s.r.o",
    "street": "Černokostelecká 604/116",
    "support_email": "info",
    "support_phone": "+36 1 550 7665",
    "support_phone_link": "+3615507665",
    "this_site_is_used_to_provide_services": "Annak érdekében, hogy weboldalunkon tökéletes böngészési élményt nyújthassunk, cookie-kat (sütiket) használunk a hirdetések személyre szabásához és a forgalom elemzéséhez. A használatukhoz korábban megadott hozzájárulást visszautasíthatja",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "Pénteken 8.00-16.00.",
    "working_hours_mo_th": "Hétfőtől csütörtökig 8.00-16.30.",
    "workshop": "Vállalkozás helye:"
},
    "de": {
    "according_to_act_on_registration_of_sales": "Nach dem Gesetz über Verkaufsaufzeichnungen ist der Verkäufer verpflichtet, dem Käufer eine Quittung auszustellen. Gleichzeitig ist er verpflichtet, die eingegangenen Verkäufe online bei der Steuerverwaltung zu registrieren; im Falle eines technischen Ausfalls spätestens innerhalb von 48 Stunden.",
    "bug_noticed": "Hast du einen Fehler in unserem E-Shop entdeckt? Bitte lasse uns es hier wissen:",
    "bug_report_cta": "Fehler melden",
    "city": "108 00 Prag 10",
    "country": "Tschechische Republik",
    "customer_centre": "Kundenservice",
    "dic": "Umsatzsteuer-Identifikationsnummer: CZ24836451",
    "here": "hier.",
    "ic": "ID: 24836451",
    "messenger": "Messenger",
    "more_information": "Weitere Informationen",
    "producer": "Mixit s.r.o",
    "street": "Černokostelecká 604/116",
    "support_email": "info",
    "support_phone": "+49 221 8461 5111",
    "support_phone_link": "+49 221 8461 5111",
    "this_site_is_used_to_provide_services": "Um dir ein optimales Surferlebnis zu bieten, verwenden wir Cookies (die Schokoladen-Cookies), um Anzeigen zu personalisieren und den Datenverkehr zu analysieren. Du kannst deine vorherige Zustimmung zur Verwendung verweigern",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "Freitag 7.00 bis 15.00 Uhr.",
    "working_hours_mo_th": "Montag bis Freitag 7.00 bis 15.00 Uhr.",
    "workshop": "Niederlassung:"
},
    "at": {
    "according_to_act_on_registration_of_sales": "Nach dem Gesetz über Verkaufsaufzeichnungen ist der Verkäufer verpflichtet, dem Käufer eine Quittung auszustellen. Gleichzeitig ist er verpflichtet, die eingegangenen Verkäufe online bei der Steuerverwaltung zu registrieren; im Falle eines technischen Ausfalls spätestens innerhalb von 48 Stunden.",
    "bug_noticed": "Hast du einen Fehler in unserem E-Shop entdeckt? Bitte lasse uns es hier wissen:",
    "bug_report_cta": "Fehler melden",
    "city": "108 00 Prag 10",
    "country": "Tschechische Republik",
    "customer_centre": "Kundenservice",
    "dic": "Umsatzsteuer-Identifikationsnummer: CZ24836451",
    "here": "hier.",
    "ic": "ID: 24836451",
    "messenger": "Messenger",
    "more_information": "Weitere Informationen",
    "producer": "Mixit s.r.o",
    "street": "Černokostelecká 604/116",
    "support_email": "info",
    "support_phone": "+49 221 8461 5111",
    "support_phone_link": "+49 221 8461 5111",
    "this_site_is_used_to_provide_services": "Um dir ein optimales Surferlebnis zu bieten, verwenden wir Cookies (die Schokoladen-Cookies), um Anzeigen zu personalisieren und den Datenverkehr zu analysieren. Du kannst deine vorherige Zustimmung zur Verwendung verweigern",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "Freitag 7.00 bis 15.00 Uhr.",
    "working_hours_mo_th": "Montag bis Freitag 7.00 bis 15.00 Uhr.",
    "workshop": "Niederlassung:"
},
    "ro": {
    "according_to_act_on_registration_of_sales": "În conformitate cu Legea privind evidența vânzărilor, vânzătorul este obligat să elibereze o chitanță cumpărătorului. În același timp, acesta este obligat să înregistreze online vânzările primite la administratorul fiscal; în caz de defecțiune tehnică, în termen de cel mult 48 de ore.",
    "bug_noticed": "Ați observat o eroare în magazinul nostru online? Vă rugăm să ne anunțați aici:",
    "bug_report_cta": "Raportează eroare",
    "city": "108 00, Praga 10",
    "country": "Republica Cehă",
    "customer_centre": "Contact",
    "dic": "CIF: CZ24836451",
    "here": "aici.",
    "ic": "CUI: 24836451",
    "messenger": "",
    "more_information": "Mai multe informații",
    "producer": "Mixit s.r.o.",
    "street": "Černokostelecká nr. 604/116",
    "support_email": "info",
    "support_phone": "+40 373 761 377",
    "support_phone_link": "+40373761377",
    "this_site_is_used_to_provide_services": "Pentru a vă oferi o experiență de navigare perfectă, pentru personalizarea reclamelor și analiza traficului site-ului nostru folosim cookie-uri (cele de ciocolată). Puteți refuza consimțământul dumneavoastră anterior pentru utilizarea acestora.",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "",
    "working_hours_fr": "",
    "working_hours_mo_th": "De luni până vineri 9:30-15:00.",
    "workshop": "Punct de lucru:"
},
    "fr": {
    "according_to_act_on_registration_of_sales": "",
    "bug_noticed": "Avez-vous remarqué une erreur dans notre e-shop ? Merci de nous le faire savoir ici :",
    "bug_report_cta": "Signaler une erreur",
    "city": "108 00, Prague 10",
    "country": "République tchèque",
    "customer_centre": "Service client",
    "dic": "Numéro de TVA : CZ24836451",
    "here": "ici.",
    "ic": "Numéro de SIREN : 24836451",
    "messenger": "Messager",
    "more_information": "Plus d'informations",
    "producer": "Mixit s.r.o.",
    "street": "Černokostelecká 604/116",
    "support_email": "info",
    "support_phone": " 01 89 70 71 57",
    "support_phone_link": " 01 89 70 71 57",
    "this_site_is_used_to_provide_services": "Pour vous offrir une expérience parfaite lors de la navigation sur notre site, nous utilisons des cookies (les délicieux petits biscuits au chocolat) pour personnaliser les publicités et analyser le trafic. Vous pouvez retirer votre consentement préalable à leur utilisation à tout moment.",
    "whatsapp": "WhatsApp",
    "whatsapp_link": "https://wa.me/420725366466",
    "working_hours_fr": "Vendredi de 8 h à 16 h",
    "working_hours_mo_th": "Du lundi au jeudi, de 8 h à 16 h 30",
    "workshop": "Établissement :"
}
  }
</i18n>
